import React, { useMemo } from 'react';
import { FormField } from '@src/components/SmartDataTable/types';
import { PAYMENT_STATUS_OPTIONS } from '@src/pages/medical/sales/constants';
import { formatNumber } from '@src/utils/numberUtils';
import { cleanPhoneNumber, getPhoneFieldConfig } from '@src/utils/phoneUtils';
import dayjs from 'dayjs';

// Types for sale item calculations
interface Medicine {
  id: number;
  name: string;
  hsn: string;
  pack_size: string;
  pack_quantity: number;
  gst_rate: number;
}

interface Sale {
  id: number;
  patient_id: number;
  customer_name: string;
  customer_phone: string;
  date: string;
  notes: string;
  sale_items: SaleItem[];
  total_rate: number;
  discount: number;
  tax_amount: number;
  total_amount: number;
}

interface SaleItem {
  id: string | number;
  medicine_id?: number;
  medicine?: any;
  mrp?: number;
  quantity?: number;
  discount?: number;
  gst_rate?: number;
  amount?: number; // This is the base price (before GST)
  discount_amount?: number;
  discounted_base_price?: number;
  gst_amount?: number;
  net_amount?: number;
  [key: string]: any;
}

export const SalesFormFields = (): ((record?: any) => FormField[]) => {
  // Memoized optionRender functions to prevent infinite re-renders
  const patientOptionRender = useMemo(
    () => (item: any) =>
      item ? `${item.first_name} ${item.last_name} (${item.mobile_no})` : '',
    []
  );

  const patientOptionLabel = useMemo(
    () => (item: any) =>
      item ? `${item.first_name} ${item.last_name} (${item.mobile_no})` : '',
    []
  );

  const medicineOptionRender = useMemo(
    () => (option: any) => {
      // Based on actual API structure:
      // Medicine object with nested inventory: { id, name, mrp (string), inventory: { id, current_stock, location } }
      
      // Get name from top level
      const name = option?.name || '';
      
      // Get MRP from top level (it's a string in the API)
      const mrp = option?.mrp || '0';
      
      // Get stock from nested inventory object
      const currentStock = option?.inventory?.current_stock || 0;

      if (!name) return '';

      // Convert MRP string to number and format
      const mrpValue = typeof mrp === 'string' ? parseFloat(mrp) : (mrp || 0);
      const formattedMrp = mrpValue > 0 ? formatNumber(mrpValue) : '0.00';
      
      return `${name} - ₹${formattedMrp} (Stock: ${currentStock})`;
    },
    []
  );

  const medicineOptionTooltip = useMemo(
    () => (option: any) => {
      // Based on actual API structure:
      // Medicine object with nested inventory: { id, name, mrp (string), hsn, pack_size, inventory: { id, current_stock, location } }
      
      // Get values from top level medicine object
      const name = option?.name || '';
      const mrp = option?.mrp || '0';
      const hsn = option?.hsn || '';
      const packSize = option?.pack_size || '';
      const manufacturer = option?.manufacturer || '';
      
      // Get values from nested inventory object
      const currentStock = option?.inventory?.current_stock || 0;
      const location = option?.inventory?.location || '';

      if (!name) return '';

      // Convert MRP string to number and format
      const mrpValue = typeof mrp === 'string' ? parseFloat(mrp) : (mrp || 0);
      const formattedMrp = mrpValue > 0 ? formatNumber(mrpValue) : '0.00';

      const lines = [
        `Name: ${name}`,
        manufacturer ? `Manufacturer: ${manufacturer}` : null,
        hsn ? `HSN: ${hsn}` : null,
        packSize ? `Pack Size: ${packSize}` : null,
        `MRP: ₹${formattedMrp}`,
        `Stock: ${currentStock}`,
      ].filter(Boolean); // Remove null entries
      
      if (location) {
        lines.push(`Location: ${location}`);
      }

      return lines.join('\n');
    },
    []
  );

  // Memoized onValueChange handlers to prevent infinite re-renders
  const handleMedicineValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('medicine_id', value, rowData, allRows, form);
    },
    []
  );

  const handleQuantityValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('quantity', value, rowData, allRows, form);
    },
    []
  );

  const handleDiscountValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('discount', value, rowData, allRows, form);
    },
    []
  );

  const handleMrpValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('mrp', value, rowData, allRows, form);
    },
    []
  );

  const handleGstRateValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('gst_rate', value, rowData, allRows, form);
    },
    []
  );

  const handleNetAmountValueChange = useMemo(
    () => (value: any, rowData: any, allRows: any, form: any) => {
      handleColumnValueChange('net_amount', value, rowData, allRows, form);
    },
    []
  );

  const calculateBasePrice = (
    mrp: number,
    quantity: number,
    packQuantity: number,
    gstRate: number
  ): number => {
    // Validate inputs to prevent infinity/NaN
    if (
      !mrp ||
      !quantity ||
      !packQuantity ||
      mrp <= 0 ||
      quantity <= 0 ||
      packQuantity <= 0
    ) {
      return 0;
    }

    // Base Price = (MRP * quantity) / (1 + (GST% / 100))
    const totalMRP = (mrp * quantity) / packQuantity;
    const gstMultiplier = 1 + (gstRate || 0) / 100;

    if (gstMultiplier <= 0) {
      return 0;
    }

    const result = totalMRP / gstMultiplier;
    return isFinite(result) ? Math.round(result * 100) / 100 : 0;
  };

  const calculateDiscountAmount = (
    amount: number,
    discount: number
  ): number => {
    if (!amount || amount <= 0 || !discount || discount <= 0) {
      return 0;
    }
    const result = (amount * discount) / 100;
    return isFinite(result) ? Math.round(result * 100) / 100 : 0;
  };

  const calculateDiscountedAmount = (
    amount: number,
    discountAmount: number
  ): number => {
    const result = (amount || 0) - (discountAmount || 0);
    return isFinite(result) ? Math.round(Math.max(0, result) * 100) / 100 : 0;
  };

  const calculateGSTAmount = (
    discountedAmount: number,
    gstRate: number
  ): number => {
    if (
      !discountedAmount ||
      discountedAmount <= 0 ||
      !gstRate ||
      gstRate <= 0
    ) {
      return 0;
    }
    const result = (discountedAmount * gstRate) / 100;
    return isFinite(result) ? Math.round(result * 100) / 100 : 0;
  };

  const calculateNetAmount = (
    discountedAmount: number,
    gstAmount: number
  ): number => {
    const result = (discountedAmount || 0) + (gstAmount || 0);
    return isFinite(result) ? Math.round(result * 100) / 100 : 0;
  };

  // Unified calculation function for a sale item
  const calculateSaleItemTotals = (item: SaleItem): SaleItem => {
    const mrp = formatNumber(item.mrp || 0);
    const quantity = formatNumber(item.quantity || 0);
    const discount = formatNumber(item.discount || 0);
    const gstRate = formatNumber(item.gst_rate || 0);
    const packQuantity = formatNumber(item.medicine?.pack_quantity || 1); // Default to 1 to prevent division by zero

    // Calculate base price (price before GST) - this is our amount
    const amount = calculateBasePrice(mrp, quantity, packQuantity, gstRate);

    // Calculate discount amount on base price
    const discountAmount = calculateDiscountAmount(amount, discount);

    // Calculate discounted base price
    const discountedBasePrice = calculateDiscountedAmount(
      amount,
      discountAmount
    );

    // Calculate GST amount on discounted base price
    const gstAmount = calculateGSTAmount(discountedBasePrice, gstRate);

    // Calculate net amount (discounted base price + GST)
    const netAmount = calculateNetAmount(discountedBasePrice, gstAmount);

    return {
      ...item,
      amount,
      discount_amount: discountAmount,
      discounted_base_price: discountedBasePrice,
      gst_amount: gstAmount,
      net_amount: netAmount,
    };
  };

  // Helper function to calculate sale totals
  const calculateSaleTotals = (sale_items: SaleItem[], form: any) => {
    // Filter out deleted items from calculations
    const activeItems = sale_items.filter(
      (item: any) => item._action !== 'delete'
    );

    // Calculate total rate (sum of all base amounts before discount and GST)
    const totalRate = activeItems.reduce(
      (sum, item) => sum + formatNumber(item.amount || 0),
      0
    );

    // Calculate total discount amount
    const totalDiscount = activeItems.reduce((sum, item) => {
      const amount = formatNumber(item.amount || 0);
      const discount = formatNumber(item.discount || 0);
      return sum + calculateDiscountAmount(amount, discount);
    }, 0);

    // Calculate total GST amount (GST is calculated on discounted amounts)
    const totalTax = activeItems.reduce((sum, item) => {
      const amount = formatNumber(item.amount || 0);
      const discount = formatNumber(item.discount || 0);
      const gstRate = formatNumber(item.gst_rate || 0);

      // Calculate discounted amount
      const discountAmount = calculateDiscountAmount(amount, discount);
      const discountedAmount = calculateDiscountedAmount(
        amount,
        discountAmount
      );

      // Calculate GST on discounted amount
      const gstAmount = calculateGSTAmount(discountedAmount, gstRate);
      return sum + gstAmount;
    }, 0);

    // Calculate total amount: (total rate - total discount) + total tax
    const totalAmount =
      Math.round((totalRate - totalDiscount + totalTax) * 100) / 100;

    // Update form fields using only setFieldsValue to avoid circular references
    if (form && typeof form.setFieldsValue === 'function') {
      // Get current overall discount values
      const currentOverallDiscountRate =
        parseFloat(form.getFieldValue('overall_discount_rate')) || 0;
      const currentOverallDiscountAmount =
        parseFloat(form.getFieldValue('overall_discount_amount')) || 0;

      // Recalculate bill amount based on existing overall discount
      let newBillAmount = totalAmount;
      let newOverallDiscountAmount = currentOverallDiscountAmount;
      let newOverallDiscountRate = currentOverallDiscountRate;

      if (currentOverallDiscountRate > 0 && totalAmount > 0) {
        // If discount rate exists, recalculate discount amount and bill amount
        newOverallDiscountAmount = formatNumber(
          (totalAmount * currentOverallDiscountRate) / 100
        );
        newBillAmount = formatNumber(totalAmount - newOverallDiscountAmount);
      } else if (currentOverallDiscountAmount > 0 && totalAmount > 0) {
        // If discount amount exists, recalculate discount rate and bill amount
        newOverallDiscountRate = formatNumber(
          (currentOverallDiscountAmount * 100) / totalAmount
        );
        newBillAmount = formatNumber(
          totalAmount - currentOverallDiscountAmount
        );
      } else {
        // No discount, bill amount equals total amount
        newBillAmount = totalAmount;
        newOverallDiscountAmount = 0;
        newOverallDiscountRate = 0;
      }

      form.setFieldsValue({
        total_rate: Math.round(totalRate * 100) / 100,
        tax_amount: Math.round(totalTax * 100) / 100,
        total_amount: totalAmount,
        due_amount: newBillAmount,
        discount_amount: Math.round(totalDiscount * 100) / 100,
        overall_discount_rate: Math.round(newOverallDiscountRate * 100) / 100,
        overall_discount_amount:
          Math.round(newOverallDiscountAmount * 100) / 100,
        bill_amount: Math.round(newBillAmount * 100) / 100,
      });
    }
  };

  // Common onValueChange handler for all columns
  const handleColumnValueChange = useMemo(() => {
    return (
      columnKey: string,
      value: any,
      rowData: SaleItem,
      allRows: SaleItem[],
      form: any
    ) => {
      let updatedRow = { ...rowData };

      switch (columnKey) {
        case 'medicine_id':
          // Handle medicine selection from inventory
          if (value && value.data) {
            const medicine = value.data;

            if (medicine) {
              const packQuantity = formatNumber(medicine.pack_quantity || 1);
              const mrp = formatNumber(medicine.mrp || 0);

              // Only set quantity to pack_quantity if:
              // 1. No existing quantity is set, OR
              // 2. The existing quantity is the same as pack_quantity (indicating it was auto-set)
              const existingQuantity = formatNumber(rowData.quantity || 0);
              const shouldUsePackQuantity =
                existingQuantity === 0 || existingQuantity === packQuantity;
              const quantity = shouldUsePackQuantity
                ? packQuantity
                : existingQuantity;

              updatedRow = {
                ...rowData,
                inventory_id: medicine.inventory.id,
                medicine_id: medicine.id, // Store inventory.id instead of medicine.id
                medicine: {
                  id: medicine.id,
                  name: medicine.name,
                  hsn: medicine.hsn,
                  pack_size: medicine.pack_size,
                  pack_quantity: medicine.pack_quantity,
                },
                mrp: mrp,
                quantity: quantity,
                gst_rate: formatNumber(medicine.gst_rate || 0),
                discount: formatNumber(rowData.discount || 0),
              };
            }
          } else {
            // Handle medicine clearing - reset all related fields to default values
            updatedRow = {
              ...rowData,
              inventory_id: undefined,
              medicine_id: undefined,
              medicine: null,
              mrp: 0,
              quantity: 1, // Default quantity to 1
              gst_rate: 0,
              discount: formatNumber(rowData.discount || 0), // Keep existing discount
            };
          }
          break;

        case 'quantity':
        case 'mrp':
        case 'discount':
        case 'gst_rate':
          // Update the specific field
          updatedRow[columnKey] = formatNumber(value || 0);
          break;

        case 'net_amount':
          // Update the specific field
          // updatedRow['discount'] = (rowData.mrp || 0) * (rowData.quantity || 0) * (rowData.discount || 0) / 100;
          updatedRow[columnKey] = formatNumber(value || 0);
          break;

        default:
          // For other fields, just update the value
          updatedRow[columnKey] = value;
          break;
      }

      // Recalculate totals for the updated row
      updatedRow = calculateSaleItemTotals(updatedRow);

      // Update the row in the table and recalculate totals in one operation
      const rowIndex = allRows.findIndex(row => row.id === rowData.id);
      if (rowIndex !== -1) {
        const updatedRows = [...allRows];
        updatedRows[rowIndex] = updatedRow;

        // Update the table and recalculate totals without triggering additional form updates
        if (form && typeof form.setFieldsValue === 'function') {
          form.setFieldsValue({
            sale_items: updatedRows,
          });
          calculateSaleTotals(updatedRows, form);
        }
      }
    };
  }, []);

  // Function to generate form fields - not memoized to allow dynamic detection
  const generateFormFields = (record?: any): FormField[] => {
    // Check if this form is opened from prescription data
    // Check both localStorage and record parameter
    const prescriptionData = localStorage.getItem('prescriptionToSaleData');
    const isFromPrescription =
      !!prescriptionData ||
      record?._copyTimestamp ||
      record?._copyId ||
      record?._formInstanceKey ||
      record?.fromPrescription ||
      record?.sale_items?.some((item: any) => item.fromPrescription);

    return [
      // Row 1: Patient ID, Customer Name, Customer Phone
      {
        name: 'patient_id',
        label: 'Patient',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select patient...',
        searchSelectProps: {
          resource: 'patients',
          optionValue: 'id',
          optionLabel: patientOptionLabel,
          searchFields: ['first_name', 'last_name', 'mobile_no'],
          optionRender: patientOptionRender,
          allowClear: true,
        },
        layout: { span: 6 },
        defaultValue: record?.patient?.id || record?.patient_id,
        props: {
          validateTrigger: ['onBlur', 'onChange'],
          validateOnMount: false,
          validateFirst: false,
        },
        onChange: (value: any, form: any) => {
          if (value?.data) {
            const patient = value.data;

            const firstName = (patient.first_name || '').toString().trim();
            const lastName = (patient.last_name || '').toString().trim();
            const customerName = `${firstName} ${lastName}`.trim();

            // Clean and extract mobile number without country code
            const customerPhone = cleanPhoneNumber(patient.mobile_no || '');

            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                customer_name: customerName,
                customer_phone: customerPhone,
              });
            }
          } else {
            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                customer_name: '',
                customer_phone: '',
              });
            }
          }
        },
      },
      {
        name: 'customer_name',
        label: 'Customer Name',
        type: 'text',
        required: true,
        placeholder: 'Enter customer name...',
        defaultValue: record?.customer_name,
        layout: { span: 6 },
      },

      {
        name: 'customer_phone',
        label: 'Customer Phone',
        ...getPhoneFieldConfig(false, '98765 43210'),
        defaultValue: record?.customer_phone,
        layout: { span: 6 },
        props: {
          ...getPhoneFieldConfig().props,
          size: 'middle',
          className: 'compact-phone-field',
        },
        tooltip: 'Enter 10-digit mobile number (without country code)',
      },

      // Row 2: Sale Date, Notes
      {
        name: 'date',
        label: 'Sale Date',
        type: 'datetime',
        defaultValue: (() => {
          const now = dayjs();
          return now.second(0).millisecond(0);
        })(),
        props: {
          showShortcuts: false,
          dateFormat: 'DD-MM-YYYY hh:mm A',
        },
        required: true,
        layout: { span: 6 },
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'text',
        required: false,
        placeholder: 'Enter any notes...',
        layout: { span: 12 },
      },

      // Row 3: Sale Items Table
      {
        name: 'sale_items',
        label: 'Sale Items',
        type: 'table',
        required: true,
        tableProps: {
          title: '',
          addButtonText: 'Add Medicine',
          minRows: 1,
          showRowNumbers: true,
          showAddButton: true,
          addButtonPosition: 'footer',
          summaryRow: {
            enabled: true,
            fields: ['amount', 'net_amount'],
          },
          columns: [
            {
              key: 'medicine',
              title: 'Medicine',
              dataIndex: 'medicine_id',
              width: 200,
              maxWidth: 200,
              field: {
                name: 'medicine_id',
                label: 'Medicine',
                type: 'resourceSelect',
                required: true,
                placeholder: 'Select medicine...',
                searchSelectProps: {
                  resource: 'medicines',
                  optionValue: 'id',
                  searchFields: ['name', 'manufacturer', 'hsn'],
                  optionRender: medicineOptionRender,
                  optionTooltip: medicineOptionTooltip,
                  queryOptions: {
                    enabled: true,
                    staleTime: 0,
                    cacheTime: 0,
                    refetchOnWindowFocus: true,
                    refetchOnMount: true,
                  },
                },
              },
              onValueChange: handleMedicineValueChange,
            },
            {
              key: 'quantity',
              title: 'Quantity',
              dataIndex: 'quantity',
              width: 100,
              field: {
                name: 'quantity',
                label: 'Quantity',
                type: 'number',
                required: true,
                placeholder: '1',
                validation: { min: 1 },
                align: 'right',
              },
              onValueChange: handleQuantityValueChange,
            },
            {
              key: 'amount',
              title: 'Amount',
              dataIndex: 'amount',
              width: 100,
              field: {
                name: 'amount',
                label: 'Amount',
                type: 'number',
                format: 'decimal',
                decimalPlaces: 2,
                required: true,
                placeholder: '0.00',
                validation: { min: 0 },
                align: 'right',
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? numValue.toFixed(2) : '0.00';
              },
            },
            {
              key: 'gst_rate',
              title: 'GST %',
              dataIndex: 'gst_rate',
              width: 80,
              field: {
                name: 'gst_rate',
                label: 'GST %',
                type: 'number',
                required: false,
                placeholder: '0.00',
                defaultValue: 0,
                validation: { min: 0 },
                align: 'right',
                format: 'percentage',
                disabled: true, // Auto-filled from medicine selection
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? numValue.toFixed(2) : '0.00';
              },
            },
            {
              key: 'mrp',
              title: 'MRP',
              dataIndex: 'mrp',
              width: 100,
              field: {
                name: 'mrp',
                label: 'MRP',
                type: 'number',
                align: 'right',
                format: 'decimal',
                decimalPlaces: 2,
                required: false,
                defaultValue: 0,
                placeholder: '0.00',
                validation: { min: 0 },
                disabled: true, // Auto-filled from medicine selection
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? numValue.toFixed(2) : '0.00';
              },
            },
            {
              key: 'discount',
              title: 'Discount %',
              dataIndex: 'discount',
              width: 120,
              field: {
                name: 'discount',
                label: 'Discount %',
                type: 'number',
                align: 'right',
                format: 'percentage',
                required: false,
                placeholder: '0',
                defaultValue: 0,
                validation: { min: 0, max: 100 },
              },
              onValueChange: handleDiscountValueChange,
            },
            {
              key: 'net_amount',
              title: 'Net Amount',
              dataIndex: 'net_amount',
              width: 120,
              field: {
                name: 'net_amount',
                label: 'Net Amount',
                type: 'number',
                required: false,
                defaultValue: 0,
                placeholder: '0.00',
                validation: { min: 0 },
                align: 'right',
                format: 'currency',
                decimalPlaces: 2,
                disabled: true, // Auto-calculated, not editable
                readOnly: true,
              },
              render: (value: any) => {
                const numValue = parseFloat(value) || 0;
                return isFinite(numValue) ? `₹${numValue.toFixed(2)}` : '₹0.00';
              },
            },
          ],
          onSummaryChange: (summary: any) => {
            // This will be handled by the form's onSummaryChange
          },
          onRowDelete: (deletedRow: any, allRows: any[], form: any) => {
            // Immediate recalculation when a row is deleted
            calculateSaleTotals(allRows, form);
          },
        },
        layout: { span: 24 },
        // Add transform functions for proper data loading in edit mode
        transform: {
          input: (value: any) => {
            // Handle existing data when loading for edit
            if (Array.isArray(value) && value.length > 0) {
              // Process each item to ensure proper data structure
              const processedItems = value.map((item: any, index: number) => {
                // Ensure medicine and inventory data is properly structured
                let medicine = item.medicine;
                if (!medicine && item.medicine_id) {
                  // If medicine object is missing but we have an ID, create a proper structure
                  medicine = {
                    id: item.medicine_id,
                    name:
                      item.medicine_name ||
                      item.name ||
                      `Medicine ID: ${item.medicine_id}`,
                    hsn: item.hsn || '',
                    pack_size: item.pack_size || '',
                    pack_quantity: item.pack_quantity || 1,
                    mrp: item.mrp || 0,
                    gst_rate: item.gst_rate || 0,
                  };
                } else if (medicine && !medicine.id && item.medicine_id) {
                  // Ensure medicine has proper ID
                  medicine = {
                    ...medicine,
                    id: item.medicine_id,
                  };
                }

                const processedItem = {
                  ...item,
                  id: item.id || `temp_${Date.now()}_${index}`,
                  // Use inventory_id for the medicine_id field in sales
                  medicine_id: item.inventory_id || item.medicine_id,
                  inventory_id: item.inventory_id || item.medicine_id,
                  medicine: medicine,
                  // Store original medicine name for display
                  medicine_name:
                    medicine?.name || item.medicine_name || item.name,
                  // Ensure all required fields have default values
                  mrp: formatNumber(item.mrp || medicine?.mrp || 0),
                  quantity: formatNumber(
                    item.quantity || medicine?.pack_quantity || 1
                  ),
                  discount: formatNumber(item.discount || 0),
                  gst_rate: formatNumber(
                    item.gst_rate || medicine?.gst_rate || 0
                  ),
                  amount: formatNumber(item.amount || 0),
                  discount_amount: formatNumber(item.discount_amount || 0),
                  discounted_base_price: formatNumber(
                    item.discounted_base_price || 0
                  ),
                  gst_amount: formatNumber(item.gst_amount || 0),
                  net_amount: formatNumber(item.net_amount || 0),
                };

                return processedItem;
              });

              return processedItems;
            }
            return value || [];
          },
          output: (value: any) => {
            // Handle form submission - clean up temporary fields
            if (Array.isArray(value)) {
              return value.map((item: any) => {
                const cleanItem = { ...item };
                // Remove temporary IDs if they exist
                if (
                  typeof cleanItem.id === 'string' &&
                  cleanItem.id.startsWith('temp_')
                ) {
                  delete cleanItem.id;
                }
                // Ensure proper field mapping for submission
                if (cleanItem.inventory_id && !cleanItem.medicine_id) {
                  cleanItem.medicine_id = cleanItem.inventory_id;
                }
                return cleanItem;
              });
            }
            return value;
          },
        },
        onChange: (value: any, form: any) => {
          // Recalculate totals whenever the table data changes (including deletions)
          if (value && Array.isArray(value)) {
            calculateSaleTotals(value, form);
          } else {
            // If no items, clear all totals
            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                total_rate: 0,
                tax_amount: 0,
                total_amount: 0,
                due_amount: 0,
                discount_amount: 0,
              });
            }
          }
        },
        // Add onMount to trigger calculations when form loads with prescription data
        onMount: (form: any) => {
          // Trigger calculation after a short delay to ensure all fields are loaded
          setTimeout(() => {
            const saleItems = form.getFieldValue('sale_items');
            if (saleItems && Array.isArray(saleItems) && saleItems.length > 0) {
              calculateSaleTotals(saleItems, form);
            }
          }, 100);
        },
      },

      // Row 4: Financial Details
      {
        name: 'total_rate',
        label: 'Total Rate',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true, // Read-only, calculated from items
        },
      },
      {
        name: 'tax_amount',
        label: 'Tax Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true, // Read-only, calculated from items
        },
      },
      {
        name: 'total_amount',
        label: 'Total Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          disabled: true, // Read-only, calculated from items
        },
      },
      {
        name: 'overall_discount_rate',
        label: 'Overall Discount %',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          min: 0,
          max: 100,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          if (baseAmount > 0) {
            const discountAmount = formatNumber(
              (baseAmount * (value || 0)) / 100
            );
            const billAmount = formatNumber(baseAmount - discountAmount);
            form.setFieldsValue({
              overall_discount_amount: discountAmount,
              bill_amount: billAmount,
              due_amount: billAmount,
            });

            // Auto-update payment status based on bill amount
            if (billAmount === 0) {
              form.setFieldsValue({
                payment_status: 'PAID',
              });
            } else if (billAmount > 0) {
              const currentStatus = form.getFieldValue('payment_status');
              if (currentStatus === 'PAID') {
                form.setFieldsValue({
                  payment_status: 'UNPAID', //!changed from PENDING to UNPAID
                });
              }
            }
          }
        },
      },
      {
        name: 'overall_discount_amount',
        label: 'Overall Discount Amount',
        type: 'number',
        required: false,
        placeholder: '0',
        layout: { span: 6 },
        props: {
          min: 0,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          if (baseAmount > 0) {
            const discountRate = formatNumber(
              ((value || 0) * 100) / baseAmount
            );
            const billAmount = formatNumber(baseAmount - (value || 0));
            form.setFieldsValue({
              overall_discount_rate: discountRate,
              bill_amount: billAmount,
              due_amount: billAmount,
            });

            // Auto-update payment status based on bill amount
            if (billAmount === 0) {
              form.setFieldsValue({
                payment_status: 'PAID',
              });
            } else if (billAmount > 0) {
              const currentStatus = form.getFieldValue('payment_status');
              if (currentStatus === 'PAID') {
                form.setFieldsValue({
                  payment_status: 'UNPAID', //!changed from PENDING to UNPAID
                });
              }
            }
          }
        },
      },
      {
        name: 'bill_amount',
        label: 'Bill Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
        props: {
          min: 0,
          step: 0.01,
        },
        onChange: (value: any, form: any) => {
          const totalRate = parseFloat(form.getFieldValue('total_rate')) || 0;
          const totalAmount =
            parseFloat(form.getFieldValue('total_amount')) || 0;
          const baseAmount = totalAmount > 0 ? totalAmount : totalRate;

          if (baseAmount > 0 && value >= 0) {
            const discountAmount = Math.max(
              0,
              formatNumber(baseAmount - (value || 0))
            );
            const discountRate = formatNumber(
              (discountAmount * 100) / baseAmount
            );
            form.setFieldsValue({
              overall_discount_amount: discountAmount,
              overall_discount_rate: discountRate,
              due_amount: value || 0,
            });

            // Auto-update payment status based on bill amount
            if (value === 0) {
              form.setFieldsValue({
                payment_status: 'PAID',
              });
            } else if (value > 0) {
              const currentStatus = form.getFieldValue('payment_status');
              if (currentStatus === 'PAID') {
                form.setFieldsValue({
                  payment_status: 'UNPAID', //!changed from PENDING to UNPAID
                });
              }
            }
          }
        },
      },
      {
        name: 'due_amount',
        label: 'Due Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
      },
      {
        name: 'clinic_due_amount',
        label: 'Clinic Due Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        layout: { span: 6 },
      },
      {
        name: 'payment_status',
        label: 'Payment Status',
        type: 'select',
        required: true,
        hidden: true,
        options: PAYMENT_STATUS_OPTIONS,
        defaultValue: 'UNPAID',
        layout: { span: 0 },
        style: { display: 'none' },
        props: {
          size: 'middle',
        },
      },
    ] as FormField[];
  };

  return generateFormFields;
};
